import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '../../lib/utils';

/**
 * Browser and password-manager autofill, switched OFF by default.
 *
 * Kai, 2026-09-06, on the Cc field: *"Issue with the chrome UI causing issues
 * with our UI"* — Chrome's address autofill ("Kai Randles / Manage
 * addresses…") drew ITS list on top of ours, so the person search underneath
 * was unreachable. Any type-to-search control that looks like a name or an
 * email hits the same heuristic.
 *
 * `autocomplete="off"` alone is not enough: Chrome ignores it when it decides
 * a field is part of an address form, and it decides that from the field's
 * name, id, label and placeholder — all of which legitimately say "email"
 * here. So the ARIA that says "this is a custom combobox, not a form field"
 * matters as much as the attribute, and the `data-*` opt-outs cover the
 * password managers, which have their own heuristics and ignore both.
 *
 * Spread BEFORE `...props`, so a field that genuinely wants autofill can say
 * so — there isn't one today, and that is the point of the default.
 */
export const NO_AUTOFILL = {
  autoComplete: 'off',
  autoCorrect: 'off',
  autoCapitalize: 'off',
  spellCheck: false,
  'data-lpignore': 'true',
  'data-1p-ignore': '',
  'data-form-type': 'other',
} as const;

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  ({ className, ...props }, ref) => (
    <input
      ref={ref}
      {...NO_AUTOFILL}
      className={cn(
        'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors',
        'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    />
  ),
);
Input.displayName = 'Input';
